import { transientStorageManager } from 'businesslayer/minutesSessionStore'

/**
 * The subset of an action's fields that drive the "unlinked action" permission
 * rules. Actions come from several shapes (serializer items, ag-grid row data,
 * form values, drawer state), so the helpers accept anything carrying these.
 */
export interface UnlinkedActionLike {
    self_manage?: boolean
    from_deleted_committee?: boolean
    /** Contact id of the action's author (from the API). Drives who may delete an unlinked action. */
    author_id?: number | string | null
}

/**
 * An "unlinked" action belongs to a committee that no longer exists / is no
 * longer visible to the user. These are only surfaced when the Unlinked Actions
 * feature is on, and — unlike normal actions — may be completed and deleted even
 * when the user isn't a committee admin and the action isn't self-managed.
 */
export const isUnlinkedAction = (action?: UnlinkedActionLike | null): boolean =>
    transientStorageManager.complete_unlinked_actions === true &&
    action?.from_deleted_committee === true

/**
 * Whether the current user may change an action's completion status: true when
 * they are a committee admin, the action is self-managed, or the action is an
 * unlinked action. Legacy / partially loaded actions where `self_manage` is
 * undefined are treated as NOT self-managed (admin / unlinked still apply).
 */
export const canCompleteAction = (
    action?: UnlinkedActionLike | null,
    isAdmin: boolean = false
): boolean => isAdmin || action?.self_manage === true || isUnlinkedAction(action)

/**
 * Whether the logged-in user is the author of the action. Ids are compared as
 * strings because they arrive as number (API) or string (form / grid state).
 * A missing author id or missing current user never matches.
 */
export const isActionAuthor = (action?: UnlinkedActionLike | null): boolean => {
    const authorId = action?.author_id
    const currentUserId = transientStorageManager.currentUser?.id
    if (authorId == null || currentUserId == null) {
        return false
    }
    return String(authorId) === String(currentUserId)
}

/**
 * Whether the current user may delete an action:
 *  - Old flow (unchanged): a committee admin may delete any action.
 *  - New flow: when the Unlinked Actions feature is on, a non-admin may delete
 *    an unlinked action ONLY when they are its author (author_id === current
 *    user). This stops a non-admin from deleting other users' orphaned actions.
 */
export const canDeleteAction = (
    action?: UnlinkedActionLike | null,
    isAdmin: boolean = false
): boolean => isAdmin || (isUnlinkedAction(action) && isActionAuthor(action))
